import { useMemo } from 'react';
import { TextInput } from './Field.tsx';
import { MultiSelect } from './MultiSelect.tsx';
import { ADMIN_OWNER, type OwnerFilterCriteria } from '../lib/ownerFilter.ts';

type CompanyOption = { id: string; company_name: string };
type StatusOption = { value: string; label: string };

/**
 * Controlled filter strip for the unified drivers/assets lists, laid out as a
 * single row (stacking only on narrow screens): a multi-select owner facet
 * ("Operating company" + every contractor company), a multi-select status
 * facet, and free-text search. The parent owns `criteria` and applies it with
 * filterByOwner(). Each facet is multi-select — picking nothing means "no
 * filter"; picking several ORs them together.
 */
export function OwnerFilterBar({
  criteria,
  onChange,
  companies,
  statusOptions,
  searchPlaceholder = 'Search…',
}: {
  criteria: OwnerFilterCriteria;
  onChange: (next: OwnerFilterCriteria) => void;
  companies: CompanyOption[];
  statusOptions: StatusOption[];
  searchPlaceholder?: string;
}) {
  const set = (patch: Partial<OwnerFilterCriteria>) => onChange({ ...criteria, ...patch });

  // Owner options: the operating company (ADMIN_OWNER sentinel) first, then each
  // contractor company. `values` carries ids; `labels` maps them to names.
  const ownerValues = useMemo(() => [ADMIN_OWNER, ...companies.map((c) => c.id)], [companies]);
  const ownerLabels = useMemo(
    () => ({
      [ADMIN_OWNER]: 'Operating company',
      ...Object.fromEntries(companies.map((c) => [c.id, c.company_name])),
    }),
    [companies],
  );

  const statusValues = useMemo(() => statusOptions.map((s) => s.value), [statusOptions]);
  const statusLabels = useMemo(
    () => Object.fromEntries(statusOptions.map((s) => [s.value, s.label])),
    [statusOptions],
  );

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <MultiSelect
        label="Owners"
        values={ownerValues}
        labels={ownerLabels}
        selected={criteria.ownerIds}
        onChange={(ownerIds) => set({ ownerIds })}
      />
      <MultiSelect
        label="Statuses"
        values={statusValues}
        labels={statusLabels}
        selected={criteria.statuses}
        onChange={(statuses) => set({ statuses })}
      />
      <div className="relative flex-1">
        <TextInput
          type="search"
          placeholder={searchPlaceholder}
          value={criteria.query}
          onChange={(e) => set({ query: e.target.value })}
        />
      </div>
    </div>
  );
}
